import { SUPPORTED_LANGUAGES, useLang } from '@inflearn/i18n-tools';
import { merge } from 'lodash-es';
import queryString from 'query-string';
import { useMemo } from 'react';

import {
  API_ENDPOINT,
  type ClientApiV1GuidesQueryString,
  useClientApiV1GuidesQuery,
} from '../../resources/@types/accountSchema';
import { HAS_GUIDE_LANG } from '../utils/i18n/hasGuideLang';

const isPartners = (type: ClientApiV1GuidesQueryString['type']) => {
  return (
    type === 'MARKETING_PARTNERS_TERMS' ||
    type === 'MARKETING_PARTNERS_POLICY' ||
    type === 'MARKETING_PARTNERS_GUIDE'
  );
};

// 비즈니스 이용약관은 한국어 본문만 있다. 다른 언어로 요청하면 빈 본문이 온다.
const isKoreanOnly = (type: ClientApiV1GuidesQueryString['type']) => type === 'BUSINESS_TERMS';

function useClientGuidesQuery(
  guidesQueryString: ClientApiV1GuidesQueryString,
  options?: Parameters<typeof useClientApiV1GuidesQuery>[0]['options'],
) {
  const { isKo, lang } = useLang();

  const guideLanguage = useMemo(() => {
    if (isKoreanOnly(guidesQueryString.type)) {
      return SUPPORTED_LANGUAGES.ko;
    }

    if (HAS_GUIDE_LANG.includes(lang)) {
      return lang;
    }

    if (isKo) {
      return SUPPORTED_LANGUAGES.ko;
    }

    return SUPPORTED_LANGUAGES.en;
  }, [lang, isKo, guidesQueryString.type]);

  return useClientApiV1GuidesQuery({
    queryKey: getGuidesQueryKey(guidesQueryString),
    url: `${API_ENDPOINT.CLIENT_V1_GUIDES}?${queryString.stringify(
      merge({ lang: isPartners(guidesQueryString.type) ? lang : guideLanguage }, guidesQueryString),
    )}`,
    options,
  });
}

export const getGuidesQueryKey = (guidesQueryString: ClientApiV1GuidesQueryString) => [
  API_ENDPOINT.CLIENT_V1_GUIDES,
  guidesQueryString,
];

export default useClientGuidesQuery;
